import { api, URLS } from 'js/modules/axios'
import { computed, ref, unref } from 'vue'
import { Dialog } from 'quasar'
import { NotifyAlert, NotifyError } from '../boot/Notify'
import { templateUnificadoService } from '../services/templateUnificado.service'
import { useI18n } from 'vue-i18n'
import ModalConfirm from 'components/Modal/ModalConfirm.vue'
import ModalSweetAlert from 'components/Modal/ModalSweetAlert.vue'
import useApiError from './useApiError'

export default function useTemplateUnificado() {
  const { t } = useI18n()

  const {
    getTemplate,
    getAvisos,
    getAnosDisponiveis,
    postMultipleUpdate,
    postJustificativas,
    patchJustificativas,
    postGrupoTemplateUnificado,
    postValidacoes,
    postAvisos,
  } = templateUnificadoService()

  const columns = ref([])
  const templates_por_mes_ids = ref([])
  const rowsChanged = ref({})
  const fabricante = ref(null)
  const grupo = ref(null)
  const step = ref(1)
  const editingCellKey = ref(null)

  const solicitacaoTemplate = ref(null)
  const anoSelecionado = ref(null)
  const anosDisponiveis = ref([])
  const currentStepParams = ref({})

  let requestVersion = 0
  const titulo = ref('')
  const avisos = ref([])
  const validacoes = ref({})

  const validacoesComErro = computed(() =>
    Object.values(validacoes.value).flatMap((arr) =>
      arr.filter((item) => item.error)
    )
  )

  const haveModfications = computed(
    () => Object.keys(rowsChanged.value).length > 0
  )

  const haveAvisos = computed(
    () =>
      avisos.value.length > 0 ||
      Object.keys(validacoesComErro.value).length > 0
  )

  /** @example  [{
   coluna_q0: {
      value: 'Q0 Value',
      infoText: 'Informação adicional sobre Q0',
      Input: true,
      color: 'rgba(var(--alert-success), 1)',
      backgroundColor: 'rgba(var(--alert-success), 0.1)',
    },
  }] */
  const rows = ref([])

  // Mapa para acesso direto aos itens por key
  const rowIndexMap = ref(new Map())
  const modalAtencaoRef = ref(null)
  const loading = ref({
    template: false,
    sending: false,
    avisos: false,
    validacoes: false,
    anos: false,
  })

  const tableStructure = computed(() => {
    if (!rows.value || !columns.value) return null

    // Criar um mapa de todas as células por key
    const cellsMap = new Map()

    rows.value.forEach((row, rowIndex) => {
      columns.value.forEach((col, colIndex) => {
        const cellValue = row[col.name]

        if (cellValue && cellValue.key) {
          cellsMap.set(cellValue.key, {
            colIndex,
            colName: col.name,
            colLabel: col.label,
            rowIndex,
            value: cellValue.value,
            previousValue:
              colIndex > 0
                ? row[columns.value[colIndex - 1].name]?.value
                : null,
            previousColumn:
              colIndex > 0 ? columns.value[colIndex - 1].name : null,
            previousKey:
              colIndex > 0
                ? row[columns.value[colIndex - 1].name]?.key
                : null,
            key: cellValue.key,
            fullCell: cellValue, // Dados completos da célula
          })
        }
      })
    })

    return {
      // Método para buscar valor pela key
      getValue: (key) => {
        return +cellsMap.get(key)?.value || 0
      },

      // Método para buscar valor anterior pela key
      getPreviousValue: (key) => {
        return +cellsMap.get(key)?.previousValue || 0
      },

      // Método para buscar key anterior
      getPreviousKey: (key) => {
        return cellsMap.get(key)?.previousKey || null
      },

      // Método para buscar informações completas da célula pela key
      getCellInfo: (key) => {
        return cellsMap.get(key) || null
      },

      // Mapa completo de células
      cellsMap,
    }
  })

  async function initialRequest(params = {}) {
    const version = ++requestVersion
    loading.value.template = true
    try {
      const template = await getTemplate({
        ...params,
      })
      if (version !== requestVersion) return
      createColumnsAndRows(template)
      await fetchAvisos()
    } catch (error) {
      if (version !== requestVersion) return
      useApiError(error)
    } finally {
      if (version === requestVersion) loading.value.template = false
    }
  }

  function onOpenAvisos() {
    const errors = validacoesComErro.value
    // console.log({ errors, validacoes: validacoes.value })

    modalAtencaoRef.value.setState({
      open: true,
      data: { templates_por_mes_ids, errors },
    })
  }

  async function fetchAvisos(params = {}) {
    let nextPage = 1

    try {
      loading.value.avisos = true

      while (nextPage) {
        const r = await getAvisos({ ...params, page: nextPage })

        avisos.value = [...avisos.value, ...r.results]
        nextPage = r.next
          ? new URL(r.next).searchParams.get('page')
          : null
      }
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.avisos = false
      return unref(avisos) || []
    }
  }

  async function createColumnsAndRows(template) {
    columns.value = template.columns.map((column, index) => ({
      ...column,
      label: column?.chave_traducao_label
        ? t(column?.chave_traducao_label)
        : column?.label,
      align: 'left',
      sortable: false,
      headerStyle:
        index === 0
          ? { width: '100%', minWidth: '25rem' }
          : {
              /* maxWidth: '12.5rem', textOverflow: 'ellipsis', overflow: 'hidden' */
            },
    }))

    rows.value = template.rows.map((row, index) => {
      const final = Object.entries(row).reduce(
        (acc, [key, value]) => {
          acc[key] = {
            ...value,
            value: value.value || 0,
            // deixe input randominco true ou false
            input: true,
            index,
            infoText: value?.descricao,
            // infoTitle: 'Titulo ',
            info: value?.descricao,
            // color: 'rgba(var(--alert-success), 1)',
            // backgroundColor: 'rgba(var(--alert-success), 0.1)',
            hasDescricao:
              value?.descricao || value?.chave_traducao_descricao,
          }
          return acc
        },
        {}
      )

      return final
    })

    // Criar mapa de índices para acesso direto
    buildRowIndexMap()

    templates_por_mes_ids.value = template.templates_por_mes_ids
  }

  function buildRowIndexMap() {
    rowIndexMap.value.clear()
    rows.value.forEach((row, rowIndex) => {
      Object.entries(row).forEach(([field, data]) => {
        if (data.key) {
          rowIndexMap.value.set(data.key, { rowIndex, field })
        }
      })
    })
  }

  /* copnst rows = [{"item":{"key":"","value":"Serviços finalizados conforme SLA","input":true},"coluna_q0":{"key":"total_servicos_finalizados_conforme_sla--27","value":0,"input":true},"coluna_jan":{"key":"total_servicos_finalizados_conforme_sla--26","value":0,"input":true},"coluna_abr":{"key":"total_servicos_finalizados_conforme_sla--23","value":0,"input":true},"coluna_jul":{"key":"total_servicos_finalizados_conforme_sla--20","value":0,"input":true},"coluna_ago":{"key":"total_servicos_finalizados_conforme_sla--19","value":0,"input":true}},{"item":{"key":"","value":"% Serviços de instalação conforme SLA","input":true},"coluna_q0":{"key":"servico_instalacao_conforme_sla--27","value":0,"input":true},"coluna_jan":{"key":"servico_instalacao_conforme_sla--26","value":0,"input":true},"coluna_abr":{"key":"servico_instalacao_conforme_sla--23","value":0,"input":true},"coluna_jul":{"key":"servico_instalacao_conforme_sla--20","value":0,"input":true},"coluna_ago":{"key":"servico_instalacao_conforme_sla--19","value":0,"input":true}},{"item":{"key":"","value":"% Serviços de recolhimento conforme SLA","input":true},"coluna_q0":{"key":"servico_recolhimento_conforme_sla--27","value":0,"input":true},"coluna_jan":{"key":"servico_recolhimento_conforme_sla--26","value":0,"input":true},"coluna_abr":{"key":"servico_recolhimento_conforme_sla--23","value":0,"input":true},"coluna_jul":{"key":"servico_recolhimento_conforme_sla--20","value":0,"input":true},"coluna_ago":{"key":"servico_recolhimento_conforme_sla--19","value":0,"input":true}},{"item":{"key":"","value":"% Serviços de manutenção conforme SLA","input":true},"coluna_q0":{"key":"servico_manutencao_conforme_sla--27","value":0,"input":true},"coluna_jan":{"key":"servico_manutencao_conforme_sla--26","value":0,"input":true},"coluna_abr":{"key":"servico_manutencao_conforme_sla--23","value":0,"input":true},"coluna_jul":{"key":"servico_manutencao_conforme_sla--20","value":0,"input":true},"coluna_ago":{"key":"servico_manutencao_conforme_sla--19","value":0,"input":true}}] */
  function onUpdateField(rowOrRows) {
    // Normaliza para sempre trabalhar com array
    const items = Array.isArray(rowOrRows) ? rowOrRows : [rowOrRows]

    items.forEach((row) => {
      if (!row.key) return

      // Acesso direto usando o mapa de índices - O(1) ao invés de O(n)
      const indexInfo = rowIndexMap.value.get(row.key)

      if (indexInfo) {
        const { rowIndex, field } = indexInfo
        // Atualiza diretamente o item específico
        rows.value[rowIndex][field] = {
          ...rows.value[rowIndex][field],
          value: row.value,
        }
      }

      rowsChanged.value[row.key] = row.value
    })
  }

  async function submitChangesReq() {
    loading.value.sending = true
    try {
      await postMultipleUpdate({
        ...rowsChanged.value,
        templates_por_mes_ids: templates_por_mes_ids.value,
      })
      rowsChanged.value = {}
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.sending = false
    }
  }

  async function submitAvisosReq(validacoes) {
    try {
      const _validacoes = Object.values(validacoes)
        .reduce((acc, item) => {
          item
            ?.filter((i) => {
              return (
                i.error &&
                !i.has_aviso &&
                avisos.value.find(
                  (a) => a.key_error === i.key_error
                ) == null
              )
            })
            ?.forEach((aviso) => acc?.push(aviso))
          return acc
        }, [])
        .map(async (aviso) => {
          const titleTranslation = aviso?.chave_traducao_label
            ? t(aviso.chave_traducao_label)
            : aviso.label
              ? aviso.label
              : aviso.verbose_name_campo
          return postAvisos({
            titulo: titleTranslation,
            descricao: aviso.detail,
            template_unificado: aviso.template_unificado,
            key_error: aviso.key_error,
          })
        })

      const responsePromise = await Promise.all(_validacoes)
      showModalConfirm({ text: t('changesSent') })

      return responsePromise
    } catch (error) {
      useApiError(error)
    } finally {
      // Fechar modal de atencao
      modalAtencaoRef.value.close()
    }
  }

  function submitChanges(showModal = true) {
    // if (Object.keys(rowsChanged.value).length === 0) {
    //   NotifyAlert(t('noValueChanged'))
    //   return
    // }

    if (showModal) {
      const confirmDialog = Dialog.create({
        component: ModalConfirm,
        componentProps: {
          text: t('confirmSend'),
          title: t('confirmation'),
        },
      })
      confirmDialog.onOk(async () => {
        await submitChangesReq()
        showModalConfirm({ text: t('changesSent') })
      })
    } else {
      return submitAvisosReq(validacoes.value)
    }
  }

  function onSubmitChanges() {
    if (Object.keys(validacoesComErro.value).length) onOpenAvisos()
    else submitChanges()
  }

  async function showModalConfirm(props) {
    Dialog.create({
      component: ModalSweetAlert,
      componentProps: {
        type: 'sucess',
        timeout: 2000,
        ...props,
      },
    })
  }

  async function onSubmitAlerts(params) {
    const { justificativas, avisos, isJustificativaId } = params

    const response = await submitChanges(false)
    const responseAvisosIds = response?.map((r) => r.id)
    const hasJustificativas =
      justificativas.filter((j) => j !== '').length > 0

    if (!avisos.length || !hasJustificativas) return

    const justificativa = justificativas[0]
    const avisosId = avisos.flatMap((d) => d.items).map((a) => a.id)

    const body = {
      justificativa,
      avisos: []
        .concat(avisosId, responseAvisosIds)
        .filter((v) => v != null),
    }

    try {
      if (isJustificativaId)
        await patchJustificativas(body, isJustificativaId)
      else await postJustificativas(body)

      showModalConfirm({ text: t('changesSent') })
    } catch (error) {
      useApiError(error)
    } finally {
      // Fechar modal de atencao
      modalAtencaoRef.value.close()
    }
  }

  async function onSubmitValidacoes({
    previuosBody,
    nextBody,
    cascataBody,
    configId,
  }) {
    // primeiro envia as alterações
    await submitChangesReq()

    // quem está fazendo a validação: fabricante OU usuário
    const fabricanteId = fabricante.value?.id
    const userId = window?.context?.user?.id

    const fabUserObject = fabricanteId
      ? { fabricante_id: fabricanteId }
      : { usuario_id: userId }

    // monta lista simples de requisições
    const bodies = []

    if (previuosBody) {
      bodies.push({
        body: { ...previuosBody, ...fabUserObject },
      })
    }

    if (nextBody) {
      bodies.push({
        body: { ...nextBody, ...fabUserObject },
      })
    }

    if (cascataBody?.length) {
      cascataBody.forEach((c) => {
        bodies.push({
          body: { ...c, ...fabUserObject },
        })
      })
    }

    if (!bodies.length || !configId) return

    try {
      loading.value.validacoes = true

      // Criar array de promises
      const validacoesPromises = bodies
        .filter(({ body }) => body?.template_id)
        .map(({ body }) =>
          postValidacoes(body, body?.configId || configId)
        )

      // Aguardar todas as validações
      const allResults = await Promise.all(validacoesPromises)

      // Processar todos os resultados
      allResults.forEach((res) => {
        res?.forEach((item) => {
          // item.campo vem do backend (ex: "total_servicos_finalizados_conforme_sla--27")
          const key = item.campo
          if (!key) return

          // Criar objeto de validação
          const validacao = {
            error: item.erro_validacao,
            message: item.detail,
            data_criacao: new Date().toISOString(),
            ...item,
          }

          // Se a key ainda não existe no objeto validacoes, cria um array vazio
          if (!validacoes.value[key]) {
            validacoes.value[key] = []
          }

          // Busca o índice de uma validação existente com o mesmo key_error
          const indexErroExistente = validacoes.value[key].findIndex(
            (v) => v.key_error === item.key_error
          )

          // Se encontrou, sobrescreve. Se não, adiciona
          if (indexErroExistente !== -1) {
            validacoes.value[key][indexErroExistente] = validacao
          } else {
            validacoes.value[key].push(validacao)
          }
        })
      })

    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.validacoes = false
    }
  }

  async function fetchSolicitacao(categoriaId, fabricanteId) {
    try {
      const { data } = await api.get(
        `${URLS.solicitacoesArquivos}?grupo_arquivo__categoria_arquivo=${categoriaId}&tipo_template=Template+unificado&fabricante=${fabricanteId}`
      )
      solicitacaoTemplate.value = data?.results?.at(0) || null
    } catch (error) {
      console.error(error)
      solicitacaoTemplate.value = null
    }
  }

  function onSelectFabricante(fab, categoriaId) {
    fabricante.value = fab
    if (fab?.id) {
      fetchSolicitacao(categoriaId, fab.id)
    } else {
      solicitacaoTemplate.value = null
    }
  }

  async function fetchSolicitacaoByGrupo(categoriaId, grupoId) {
    try {
      const { data } = await api.get(
        `${URLS.solicitacoesArquivos}?grupo_arquivo__categoria_arquivo=${categoriaId}&tipo_template=Template+unificado&grupo_template_unificado=${grupoId}`
      )
      solicitacaoTemplate.value = data?.results?.at(0) || null
    } catch (error) {
      console.error(error)
      solicitacaoTemplate.value = null
    }
  }

  function onSelectGrupo(grp, categoriaId) {
    grupo.value = grp
    if (grp?.id) {
      fetchSolicitacaoByGrupo(categoriaId, grp.id)
    } else {
      solicitacaoTemplate.value = null
    }
  }

  async function fetchAnosDisponiveis(params = {}) {
    loading.value.anos = true
    try {
      const anos = await getAnosDisponiveis(params)
      anosDisponiveis.value = anos
      if (anos.length > 0 && !anoSelecionado.value) {
        anoSelecionado.value = anos[0]
      }
    } catch (error) {
      useApiError(error)
    } finally {
      loading.value.anos = false
    }
  }

  async function onChangeAno(ano) {
    if (ano === anoSelecionado.value && rows.value.length > 0) return

    if (haveModfications.value) {
      const confirmDialog = Dialog.create({
        component: ModalConfirm,
        componentProps: {
          text: t('modificationsUnsavedMessage'),
          title: t('confirmation'),
        },
      })

      confirmDialog.onOk(() => {
        anoSelecionado.value = ano
        resetData()
        initialRequest({ ...currentStepParams.value, ano })
      })

      return
    }

    anoSelecionado.value = ano
    resetData()
    initialRequest({ ...currentStepParams.value, ano })
  }

  async function onChangeStep({
    type,
    newGroup,
    grupoExistente,
    solicitacaoID,
    categoriaID,
  }) {
    const props = {
      solicitacao_arquivo: solicitacaoID,
      categoria_arquivo: categoriaID,
    }

    if (type === 'especifico') {
      props.fabricante = fabricante.value?.id
      titulo.value = fabricante.value?.nome || ''
    } else if (type === 'grupo') {
      props.grupo = grupo.value?.id
      titulo.value = grupo.value?.nome || ''

      if (newGroup) {
        try {
          const _grupo = await postGrupoTemplateUnificado({
            nome: newGroup,
            categoria_arquivo: categoriaID,
          })
          props.grupo = _grupo.id
          props.solicitacao_arquivo = _grupo.solicitacao_arquivo_id
          titulo.value = _grupo?.nome || ''
        } catch (error) {
          useApiError(error)
          NotifyError("Nenhum gerador de solicitação padrão foi configurado para esta categoria.")
          return
        }
      }
    }
    // reseta alteracoes caso o usuario volte e mude
    rowsChanged.value = {}
    step.value = 2

    currentStepParams.value = { ...props }

    await fetchAnosDisponiveis({
      solicitacao_arquivo: props.solicitacao_arquivo,
      fabricante: props.fabricante,
      grupo: props.grupo,
    })

    await initialRequest({ ...props, ano: anoSelecionado.value })
  }

  function resetData() {
    rowsChanged.value = {}
    templates_por_mes_ids.value = []
    columns.value = []
    rows.value = []
  }

  function onBackStep() {
    resetData()
    anoSelecionado.value = null
    anosDisponiveis.value = []
    currentStepParams.value = {}
    step.value = 1
  }

  function beforeUnloadHandler(event) {
    if (haveModfications.value) {
      const message = t('modificationsUnsavedMessage')
      event.preventDefault()
      event.returnValue = message
      return message
    }
  }

  return {
    loading,
    initialRequest,
    fetchAvisos,
    columns,
    rows,
    templates_por_mes_ids,
    avisos,
    validacoes,
    validacoesComErro,
    onOpenAvisos,
    modalAtencaoRef,
    onUpdateField,
    rowsChanged,
    submitChanges,
    onSubmitChanges,
    onSubmitAlerts,
    onSubmitValidacoes,
    beforeUnloadHandler,
    haveModfications,
    haveAvisos,
    fabricante,
    grupo,
    step,
    titulo,
    onChangeStep,
    editingCellKey,
    onBackStep,
    anoSelecionado,
    anosDisponiveis,
    onChangeAno,
    tableStructure,
    solicitacaoTemplate,
    fetchSolicitacao,
    onSelectFabricante,
    onSelectGrupo,
  }
}
